import { useRef, useState, useEffect } from 'react'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import AddBoxIcon from '@mui/icons-material/AddBox'
import {
  useNavigate,
  useBlocker,
  useParams,
  useSubmit,
  Outlet,
  useActionData,
} from 'react-router-dom'
import { FormProvider, useForm, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { zodResolver } from '@hookform/resolvers/zod'

import { getEventDetails, setOptions } from '../../../../store.slice'
import { useAppDispatch, useAppSelector } from '../../../../store'
import { createUniqueDeepClone } from '../../../../utils'
import { useContextStep } from '../CreateEvents.Context'
import Form from '../../../root/components/Form'
import { onSubmitData } from '../CreateEvent.utils'
import { ModifyOptionsSchema } from '../../../events/Events.schema'
import { defaultOption } from './components/Option.utils'
import OptionForm from './components/OptionForm'
import { LoaderData } from '../../../../types'
import { submitEvent } from '../CreateEvents.action'

export default function ConditionsStep4() {
  const { t } = useTranslation('events')
  const navigate = useNavigate()
  const submit = useSubmit()
  const dispatch = useAppDispatch()
  const formRef = useRef<HTMLFormElement>(null)

  const { eventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const actionData = useActionData() as LoaderData<ReturnType<typeof submitEvent>> | undefined
  const [disableSubmit, setDisableSubmit] = useState(false)

  const [step, setStep] = useContextStep()
  const eventDetails = useAppSelector(state => getEventDetails(state)(eventId))

  const methods = useForm<ModifyOptionsSchema>({
    defaultValues: {
      options: eventDetails.options ?? [],
    },
    resolver: zodResolver(ModifyOptionsSchema),
  })
  const { control, getValues, setValue } = methods

  useEffect(() => {
    setDisableSubmit(false)
    if (actionData && 'success' in actionData) {
      setValue('options', actionData.options)
    }
  }, [actionData, setValue])

  const {
    fields: optionsFields,
    append: appendOption,
    remove: removeOption,
  } = useFieldArray({
    control,
    name: `options`,
  })

  useBlocker(({ nextLocation }) => {
    const values = getValues()
    dispatch(setOptions({ data: values.options, key: eventId }))

    if (!nextLocation.pathname.includes('/admin/events/')) {
      return !window.confirm(
        `${t(
          'Êtes-vous sûr de vouloir quitter cette page ? Des modifications non enregistrées seront perdues'
        )}.`
      )
    }
    return false
  })

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const values = getValues()
    const data = {
      ...eventDetails,
      options: values.options,
    }
    dispatch(setOptions({ data: values.options, key: eventId }))
    onSubmitData(data, eventId, submit, 'conditions', setDisableSubmit)
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={onSubmitForm} ref={formRef}>
        <Stack direction="column" spacing={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <h1>{t("Modalités d'inscription")}</h1>
            <Stack
              direction="row"
              spacing={{ xs: 1, sm: 2, md: 3 }}
              sx={{
                position: 'fixed',
                zIndex: '10',
                right: '3rem',
              }}
            >
              <Button
                startIcon={<AddBoxIcon />}
                variant="contained"
                onClick={event => {
                  event.preventDefault()
                  const newOption = createUniqueDeepClone(defaultOption())
                  appendOption(newOption)
                  navigate(`/admin/events/${eventId}/conditions/${newOption.id}`)
                }}
                sx={{ pointerEvents: 'all' }}
              >
                {t('Option')}
              </Button>
            </Stack>
          </Stack>
          <Divider />
        </Stack>

        <Stack gap="1rem" flex="1 0 auto">
          {optionsFields.map((option, index) => (
            <OptionForm key={option.id} optionKey={index} onRemove={() => removeOption(index)} />
          ))}
        </Stack>

        <Stack sx={{ minHeight: '3.5rem' }} />
        <Stack
          direction="row"
          padding="0 2rem 0.5rem"
          sx={{
            position: 'fixed',
            zIndex: 1,
            width: '100%',
            bottom: { xs: '7rem', md: '3rem', lg: '2.5rem' },
            left: 0,
            pointerEvents: 'none',
          }}
        >
          <Stack minWidth={{ xs: '0', md: '340px', lg: '400px' }} />
          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 2, md: 3 }}
            justifyContent="space-between"
            padding="0 1rem"
            flex="2 0 auto"
          >
            <Button
              onClick={event => {
                event.preventDefault()
                if (window.confirm(t('Êtes-vous sûr de vouloir annuler ?'))) navigate('/dashboard')
              }}
              sx={{ pointerEvents: 'all' }}
            >
              {t('Annuler')}
            </Button>
            <Stack direction="row" spacing={{ xs: 1, sm: 2, md: 3 }}>
              <Button
                variant="contained"
                onClick={() => {
                  navigate(`/admin/events/${eventId}/logistic`)
                }}
                sx={{ pointerEvents: 'all' }}
              >
                {t('Précédent')}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (step < 5) {
                    setStep(5)
                  }
                  navigate(`/admin/events/${eventId}/validation`)
                }}
                sx={{ pointerEvents: 'all' }}
              >
                {t('Suivant')}
              </Button>
              <Button
                disabled={step < 5}
                loading={disableSubmit}
                variant="contained"
                type="submit"
                sx={{ pointerEvents: 'all' }}
              >
                {t('Enregistrer')}
              </Button>
            </Stack>
          </Stack>
        </Stack>

        <Outlet context={{ removeOption }} />
      </Form>
    </FormProvider>
  )
}
